import { useCallback, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { PlayPracticeGameDto } from "@nestcoinco/hyper-api-gateway-api-client";
import { useAtom, useSetAtom } from "jotai";
import { useNavigate } from "react-router-dom";

import { QUERY_KEYS } from "../constants";
import { handlePlayPracticeGame } from "../../api-clients/tournamentsDefaultClient";
import { GameFlowState, GameType } from "../../utils/constants";
import {
  gameTypeAtom,
  gameFlowStateAtomWithStorage,
} from "../../jotai/atoms/games";
import { practiceGameEntryAtom } from "../../jotai/atoms/tournament";
import { ROUTES } from "../../utils/constants";
import useAnalytics from "../../hooks/useAnalytics";

function usePlayPracticeGameMutation(
  dto?: PlayPracticeGameDto,
  redirectUrl?: string,
  skipNavigation?: boolean,
) {
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const [, setPracticeGameEntry] = useAtom(practiceGameEntryAtom);
  const setGameType = useSetAtom(gameTypeAtom);
  const [, setGameFlowState] = useAtom(gameFlowStateAtomWithStorage);
  const [gameUrl, setGameUrl] = useState<string>();

  const mutation = useMutation({
    mutationKey: [QUERY_KEYS.TOURNAMENT.PLAY_PRACTICE_GAME],
    mutationFn: () => handlePlayPracticeGame(dto),
    onSuccess: (entry) => {
      const { name: gameName, slug: gameSlug } = entry.game;
      analytics.sendInitiateGameEvent({
        entry_fee: 0,
        game_name: gameName,
        game_mode: GameType.PRACTICE,
      });
      setPracticeGameEntry(entry);
      setGameType(GameType.PRACTICE);
      setGameFlowState(GameFlowState.STARTING_GAME);

      if (skipNavigation) {
        setGameUrl(entry.gamePlayUrl);
      } else {
        const url =
          redirectUrl ||
          `${ROUTES.GAMES.ROOT}/${gameSlug}${ROUTES.ROOT.PRACTICE_GAME}`;
        navigate(url);
      }
    },
  });

  const playPracticeGame = useCallback(() => {
    mutation.mutate();
  }, [mutation]);

  return {
    mutation,
    playPracticeGame,
    gameUrl,
    isPending: mutation.isPending,
  };
}

export default usePlayPracticeGameMutation;
